import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { formatDate, getArticleById, getCategoryById } from "@/lib/news";
import { useSeo } from "@/hooks/useSeo";
import ArticleActions from "@/pages/detail/components/ArticleActions";
import ArticleContent from "@/pages/detail/components/ArticleContent";
import RelatedArticles from "@/pages/detail/components/RelatedArticles";

export default function DetailPage() {
  const { id } = useParams();
  const article = getArticleById(id || "");

  useSeo({
    title: article ? `${article.title}- 黑料官网深度解析` : "内容不存在 - 黑料官网",
    description: article
      ? article.summary
      : "该黑料信息不存在或已调整，请返回首页浏览最新动态。",
    keywords: article
      ? `${article.categoryName},黑料官网,${article.tags.join(",")}`
      : "黑料官网",
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
            内容不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            您访问的黑料信息不存在或已被调整。
          </p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = getCategoryById(article.categoryId);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[860px] px-4 py-8 md:px-6 md:py-12">
        <nav
          className="flex flex-wrap items-center gap-2 text-xs text-foreground-500"
          aria-label="面包屑导航"
        >
          <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          {category && (
            <>
              <Link
                to={`/category/${category.id}`}
                className="transition-colors hover:text-primary-600 cursor-pointer"
              >
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300" />
            </>
          )}
          <span className="text-foreground-800">正文详情</span>
        </nav>

        <article className="mt-6">
          <div className="text-center">
            <Link
              to={`/category/${article.categoryId}`}
              className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-label text-accent-900 transition-colors hover:bg-accent-200 cursor-pointer"
            >
              <i className="ri-price-tag-3-line" />
              {article.categoryName}
            </Link>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line" />
                来源：{article.source}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line" />
                热度 {article.heat}
              </span>
            </div>
          </div>

          {article.image && (
            <figure className="mt-6 overflow-hidden rounded-lg border border-background-200">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 相关配图`}
                className="h-auto w-full object-cover object-top"
              />
            </figure>
          )}

          <div className="mt-6">
            <ArticleActions articleId={article.id} />
          </div>

          <ArticleContent article={article} />

          {category && (
            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
              <i className="ri-folder-open-line text-xl text-primary-600" />
              <span className="text-sm text-foreground-600">本项所属分类：</span>
              <Link
                to={`/category/${category.id}`}
                className="flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                {category.name}
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          )}
        </article>

        <RelatedArticles article={article} />
      </div>
    </SiteLayout>
  );
}